import { useState, useEffect, useRef } from 'react';
import {
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Check,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  Activity,
} from 'lucide-react';
import { QuestionResponse, useQuestions } from '../../../hooks/useQuestions';
import {
  currentAssessmentResponse,
  hasCurrentAssessmentAnswer,
} from '../../../utils/currentAssessment';

interface FiveForcesChecklistProps {
  entityType: string;
  entityKey: string;
  companyId: string;
  title: string;
  className?: string;
  onScoreChange?: (score: number) => void;
}

export default function FiveForcesChecklist({
  entityType,
  entityKey,
  companyId,
  title,
  className = '',
  onScoreChange,
}: FiveForcesChecklistProps) {
  const { questions, loading, error, saveResponse, getQuestionResponse } = useQuestions(
    entityType,
    companyId,
    entityKey
  );

  // Local state to track optimistic updates for immediate UI feedback
  const [optimisticResponses, setOptimisticResponses] = useState<Record<string, boolean>>({});
  const [currentScore, setCurrentScore] = useState<number>(0);

  // Use ref to store the latest onScoreChange callback to avoid useEffect dependency issues
  const onScoreChangeRef = useRef(onScoreChange);
  onScoreChangeRef.current = onScoreChange;

  const handleQuestionToggle = async (questionId: string, isChecked: boolean) => {
    // Optimistically update the UI immediately
    setOptimisticResponses((prev) => ({
      ...prev,
      [questionId]: isChecked,
    }));

    const response: QuestionResponse = {
      questionId,
      entityType,
      entityKey,
      response: { selectedOptions: isChecked ? ['selected'] : [] },
    };

    const success = await saveResponse(response);
    if (!success) {
      console.error('Failed to save question response');
      // Revert optimistic update on failure
      setOptimisticResponses((prev) => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });
    } else {
      // Clear optimistic state once saved successfully (backend state will take over)
      setOptimisticResponses((prev) => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });
    }
  };

  const isQuestionChecked = (questionId: string): boolean => {
    // Check optimistic state first, then fall back to backend state
    if (optimisticResponses.hasOwnProperty(questionId)) {
      return optimisticResponses[questionId];
    }

    const answer = getQuestionResponse(questionId, entityKey);
    const response = answer ? currentAssessmentResponse(answer).response : null;
    return response?.selectedOptions?.includes('selected') === true;
  };

  // Function to get intensity icon and styling
  const getIntensityDisplay = (score: number) => {
    if (score < 0) {
      return {
        icon: Shield,
        className: 'text-gray-500',
        bgClassName: 'bg-gray-100',
        label: 'Not Analyzed',
      };
    }

    if (score >= 80) {
      return {
        icon: ShieldCheck,
        className: 'text-blue-700',
        bgClassName: 'bg-blue-100',
        label: 'Very Weak',
      };
    }

    if (score >= 60) {
      return {
        icon: ShieldCheck,
        className: 'text-green-700',
        bgClassName: 'bg-green-100',
        label: 'Weak',
      };
    }

    if (score >= 40) {
      return {
        icon: Activity,
        className: 'text-yellow-700',
        bgClassName: 'bg-yellow-100',
        label: 'Moderate',
      };
    }

    if (score >= 20) {
      return {
        icon: ShieldAlert,
        className: 'text-orange-700',
        bgClassName: 'bg-orange-100',
        label: 'Strong',
      };
    }

    return {
      icon: ShieldX,
      className: 'text-red-700',
      bgClassName: 'bg-red-100',
      label: 'Very Strong',
    };
  };

  const calculateScore = (): number => {
    let strongFactorsChecked = 0;
    let weakFactorsChecked = 0;
    let totalStrongFactors = 0;
    let totalWeakFactors = 0;

    questions.forEach((question) => {
      const isChecked = isQuestionChecked(question.id);
      const points = question.point || 0; // point should be 1 or -1

      if (points > 0) {
        // Strong factor (bad for industry attractiveness)
        totalStrongFactors++;
        if (isChecked) {
          strongFactorsChecked++;
        }
      } else if (points < 0) {
        // Weak factor (good for industry attractiveness)
        totalWeakFactors++;
        if (isChecked) {
          weakFactorsChecked++;
        }
      }
    });

    // If no questions, return neutral score
    if (questions.length === 0) return 50;

    // Calculate percentage of strong and weak factors checked
    const strongPercentage = totalStrongFactors > 0 ? strongFactorsChecked / totalStrongFactors : 0;
    const weakPercentage = totalWeakFactors > 0 ? weakFactorsChecked / totalWeakFactors : 0;

    // Start at 50% (moderate) and adjust based on factors
    // Strong factors decrease the score (bad for industry)
    // Weak factors increase the score (good for industry)
    const score = 50 + weakPercentage * 50 - strongPercentage * 50;

    return Math.round(Math.max(0, Math.min(100, score)));
  };

  // Update current score whenever questions, responses, or optimistic responses change
  useEffect(() => {
    if (questions.length > 0 && !loading) {
      const newScore = calculateScore();

      const hasAnyResponse = questions.some(
        (question) =>
          Object.prototype.hasOwnProperty.call(optimisticResponses, question.id) ||
          hasCurrentAssessmentAnswer(getQuestionResponse(question.id, entityKey))
      );

      // Set the display score (-1 for not analyzed, actual score if analyzed)
      const displayScore = hasAnyResponse ? newScore : -1;
      setCurrentScore(displayScore);

      // Report score change immediately
      if (onScoreChangeRef.current) {
        onScoreChangeRef.current(displayScore);
      }
    } else if (questions.length === 0 && !loading) {
      setCurrentScore(-1); // Show not analyzed when no questions

      // Report no analysis done
      if (onScoreChangeRef.current) {
        onScoreChangeRef.current(-1);
      }
    }
  }, [questions, optimisticResponses, loading]); // Removed onScoreChange from dependencies

  if (loading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 h-full ${className}`}>
        <div className="p-6">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 h-full ${className}`}>
        <div className="p-6">
          <div className="flex items-center justify-center h-32 text-center">
            <div>
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 h-full ${className}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <CheckCircle2 className="h-5 w-5 mr-2 text-blue-600" />
            {title}
          </h3>
          {(() => {
            const intensityDisplay = getIntensityDisplay(currentScore);
            const IconComponent = intensityDisplay.icon;
            return (
              <div
                className={`flex items-center space-x-2 px-3 py-1 rounded-lg ${intensityDisplay.bgClassName}`}
              >
                <IconComponent className={`h-4 w-4 ${intensityDisplay.className}`} />
                <span className={`text-sm font-medium ${intensityDisplay.className}`}>
                  {intensityDisplay.label}
                </span>
              </div>
            );
          })()}
        </div>

        {questions.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle2 className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No questions available</p>
          </div>
        ) : (
          <div className="space-y-6 overflow-y-auto max-h-[calc(100%-100px)]">
            {/* Group questions by points (-1 = weak factors first, 1 = strong factors) */}
            {[-1, 1].map((pointValue) => {
              const groupQuestions = questions.filter((q) => (q.point || 0) === pointValue);
              if (groupQuestions.length === 0) return null;

              const isStrongGroup = pointValue === 1;
              const groupTitle = isStrongGroup ? 'Strong because:' : 'Weak because:';
              const groupColor = isStrongGroup ? 'red' : 'green';
              const GroupIcon = isStrongGroup ? TrendingUp : TrendingDown;

              return (
                <div key={pointValue} className="space-y-3">
                  {/* Group Header */}
                  <div
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
                      isStrongGroup
                        ? 'bg-red-50 border border-red-200'
                        : 'bg-green-50 border border-green-200'
                    }`}
                  >
                    <GroupIcon
                      className={`h-4 w-4 ${isStrongGroup ? 'text-red-600' : 'text-green-600'}`}
                    />
                    <h4
                      className={`font-medium ${isStrongGroup ? 'text-red-800' : 'text-green-800'}`}
                    >
                      {title.replace(' Analysis', '')} is {groupTitle}
                    </h4>
                  </div>

                  {/* Questions in this group */}
                  <div className="space-y-2 ml-4">
                    {groupQuestions.map((question) => {
                      const isChecked = isQuestionChecked(question.id);

                      return (
                        <label
                          key={question.id}
                          className="flex items-start space-x-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <div className="relative mt-0.5">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => handleQuestionToggle(question.id, e.target.checked)}
                              className="sr-only"
                            />
                            <div
                              className={`w-5 h-5 rounded border-2 transition-all duration-200 ${
                                isChecked
                                  ? `border-${groupColor}-500 bg-${groupColor}-500`
                                  : 'border-gray-300 hover:border-gray-400'
                              }`}
                            >
                              {isChecked && <Check className="h-3 w-3 text-white m-0.5" />}
                            </div>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-800 leading-relaxed">
                              {question.content}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
